// -----------------------------------------------------------
// FloraIQ - Secoes da pagina Metodologia FloraIQ-LADM
// -----------------------------------------------------------
// Conteudo conceitual (config) + avisos de ausencia de dado real onde a
// tela antiga mostrava registros, contagens e eventos ficticios.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { NoRealData } from "@/components/shared";
import {
  ladmOverviewGroups,
  ladmMatrixRows,
  ladmOperationalFlow,
  ladmSpatialUnitTypes,
  ladmSourceTypes,
  ladmTraceabilityQuestions,
  ladmRrrTypes,
  ladmEventTypes,
  ladmValidationRules,
  ladmPriorityBadge,
  ladmPlannedProducts,
} from "@/config/relat-oper-ladm-ladm-config";

function SectionTitle({ children, hint }: { children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="mb-3">
      <h4 className="text-sm font-bold text-foreground-900">{children}</h4>
      {hint && <p className="text-xs text-foreground-500 mt-0.5">{hint}</p>}
    </div>
  );
}

function Chips({ items }: { items: string[] }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t) => (
        <span key={t} className="text-[11px] px-2 py-0.5 rounded border border-background-200 bg-background-50 text-foreground-600">{t}</span>
      ))}
    </div>
  );
}

function RealDataLink({ to, label }: { to: string; label: string }) {
  return (
    <Link to={to} className="text-primary-600 hover:underline">{label}</Link>
  );
}

export function LadmOverviewSection() {
  return (
    <div>
      <SectionTitle hint="Os cinco grupos de informação em que a metodologia organiza o cadastro arbóreo.">Estrutura da metodologia</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {ladmOverviewGroups.map((g) => (
          <div key={g.id} className="p-4 rounded-lg border border-background-200/70 bg-white">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                <i className={`${g.icon} text-blue-600`}></i>
              </div>
              <h5 className="text-sm font-semibold text-foreground-800">{g.title}</h5>
            </div>
            <p className="text-xs text-foreground-500">{g.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export function LadmMatrixSection() {
  return (
    <div>
      <SectionTitle hint="Correspondência entre os conceitos do LADM (ISO 19152) e sua adaptação no FloraIQ.">Matriz LADM-FloraIQ</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-background-200 text-left text-[10px] font-semibold text-foreground-500 uppercase">
              <th className="py-2 px-3">Conceito LADM</th>
              <th className="py-2 px-3">Adaptação FloraIQ</th>
              <th className="py-2 px-3">Módulo relacionado</th>
            </tr>
          </thead>
          <tbody>
            {ladmMatrixRows.map((r) => (
              <tr key={r.conceitoLadm} className="border-b border-background-100 align-top">
                <td className="py-2.5 px-3 text-xs font-semibold text-foreground-800 whitespace-nowrap">{r.conceitoLadm}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-600">{r.adaptacaoFloraIQ}</td>
                <td className="py-2.5 px-3 text-xs text-foreground-600 whitespace-nowrap">{r.moduloRelacionado}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function LadmFlowSection() {
  return (
    <div>
      <SectionTitle hint="Atividades previstas em cada etapa. O andamento real das ações está no Fluxo Operacional.">Fluxo metodológico</SectionTitle>
      <div className="space-y-3">
        {ladmOperationalFlow.map((s) => (
          <div key={s.step} className="flex gap-3 p-3 rounded-lg border border-background-200/70 bg-white">
            <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{s.step}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <i className={`${s.icon} text-foreground-500`}></i>
                <h5 className="text-sm font-semibold text-foreground-800">{s.title}</h5>
              </div>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                {s.activities.map((a) => (
                  <li key={a} className="text-xs text-foreground-600 flex items-start gap-1.5">
                    <i className="ri-arrow-right-s-line text-foreground-300 mt-0.5"></i>
                    {a}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-foreground-500 mt-3">
        Acompanhe as ações em <RealDataLink to="/operacional" label="Fluxo Operacional" />.
      </p>
    </div>
  );
}

export function LadmTerritorialSection() {
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle hint="Tipos de unidade espacial a que um exemplar arbóreo pode ser vinculado.">Unidades espaciais</SectionTitle>
        <Chips items={ladmSpatialUnitTypes} />
      </div>
      <NoRealData
        icon="ri-map-pin-line"
        title="Vínculos territoriais por exemplar"
        description="Aqui aparecerá a completude do vínculo de cada exemplar com unidade espacial, bairro, logradouro e área verde. Ainda não há consolidação desse indicador. O bairro e o logradouro derivados de cada exemplar estão na ficha do CTA e em Território > Bairros."
      />
      <p className="text-xs text-foreground-500">
        Dados reais: <RealDataLink to="/cta" label="CTA Arbóreo" /> · <RealDataLink to="/territorio/bairros" label="Território > Bairros" />
      </p>
    </div>
  );
}

export function LadmSourcesSection() {
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle hint="Tipos de fonte aceitos como evidência de uma informação técnica.">Tipos de fonte documental</SectionTitle>
        <Chips items={ladmSourceTypes} />
      </div>
      <div>
        <SectionTitle hint="Perguntas que o registro de cada informação deve permitir responder.">Rastreabilidade</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {ladmTraceabilityQuestions.map((q) => (
            <div key={q.question} className="flex items-center gap-2 p-2.5 rounded-lg bg-background-50/70 border border-background-100/70">
              <i className={`${q.icon} text-blue-600`}></i>
              <span className="text-xs text-foreground-700">{q.question}</span>
            </div>
          ))}
        </div>
      </div>
      <NoRealData
        icon="ri-folder-info-line"
        title="Fontes vinculadas por informação"
        description="Aqui aparecerá, para cada informação técnica, a fonte que a comprova, quem informou e quem validou. Ainda não há consolidação desse vínculo. Os documentos cadastrados estão no módulo Documentos."
      />
      <p className="text-xs text-foreground-500">
        Dados reais: <RealDataLink to="/documentos" label="Documentos" /> · <RealDataLink to="/partes" label="Partes Envolvidas" />
      </p>
    </div>
  );
}

export function LadmRrrSection() {
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle hint="Responsabilidades, restrições e autorizações (RRR) que podem incidir sobre um exemplar ou área.">Tipos de RRR</SectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {ladmRrrTypes.map((t) => (
            <div key={t.tipo} className="p-4 rounded-lg border border-background-200/70 bg-white">
              <div className="flex items-center gap-2 mb-1.5">
                <i className={`${t.icon} text-blue-600`}></i>
                <h5 className="text-sm font-semibold text-foreground-800">{t.tipo}</h5>
              </div>
              <p className="text-xs text-foreground-500 mb-2">{t.descricao}</p>
              <Chips items={t.categorias} />
            </div>
          ))}
        </div>
      </div>
      <NoRealData
        icon="ri-shield-check-line"
        title="RRR registrados"
        description="Aqui aparecerão as responsabilidades, restrições e autorizações registradas, com parte responsável, documento-fonte, prazo e situação. Ainda não há registros de RRR exibidos nesta tela."
      />
      <p className="text-xs text-foreground-500">
        Ver também: <RealDataLink to="/responsabilidades" label="Responsabilidades e Restrições" />
      </p>
    </div>
  );
}

export function LadmHistorySection() {
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle hint="Tipos de evento que compõem o histórico versionado de um exemplar arbóreo.">Eventos do histórico</SectionTitle>
        <Chips items={ladmEventTypes} />
      </div>
      <NoRealData
        icon="ri-history-line"
        title="Linha do tempo consolidada"
        description="Aqui aparecerão os eventos versionados de todos os exemplares. O histórico real de cada exemplar já está disponível na sua ficha (FCA), aberta a partir do CTA."
      />
      <p className="text-xs text-foreground-500">
        Dados reais: <RealDataLink to="/cta" label="CTA Arbóreo" /> (ficha do exemplar &gt; Histórico)
      </p>
    </div>
  );
}

export function LadmQualitySection() {
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle hint="Regras que a metodologia aplica ao cadastro, com sua prioridade.">Regras de validação</SectionTitle>
        <div className="space-y-1.5">
          {ladmValidationRules.map((r) => (
            <div key={r.regra} className="flex items-center justify-between gap-3 p-2.5 rounded-lg bg-background-50/70 border border-background-100/70">
              <span className="text-xs text-foreground-700">{r.regra}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${ladmPriorityBadge[r.prioridade].className}`}>
                {ladmPriorityBadge[r.prioridade].label}
              </span>
            </div>
          ))}
        </div>
      </div>
      <NoRealData
        icon="ri-check-double-line"
        title="Resultado das regras metodológicas"
        description="Aqui aparecerão, por regra, os registros avaliados, conformes e inconformes. Esse cálculo ainda não existe. As inconsistências reais do cadastro estão em Qualidade de Dados."
      />
      <p className="text-xs text-foreground-500">
        Dados reais: <RealDataLink to="/qualidade" label="Qualidade de Dados" />
      </p>
    </div>
  );
}

export function LadmProductsSection() {
  return (
    <div>
      <SectionTitle hint="Produtos previstos pela metodologia. A geração de relatórios ainda não está disponível.">Produtos e relatórios previstos</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ladmPlannedProducts.map((p) => (
          <div key={p.id} className="p-3 rounded-lg border border-background-200/70 bg-white">
            <p className="text-xs font-semibold text-foreground-800">{p.title}</p>
            <p className="text-[11px] text-foreground-500 mt-1">{p.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
